import { ArrowLeftRight, GripVertical, History, Link2, RotateCcw, Save, SaveAll, Search, Share2, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { authFetch, useAuth } from '../auth'
import { loadCalculatorBootstrap, peekCalculatorBootstrap } from '../calculatorBootstrapCache'
import { partitionCalculatorRecords, type CalculatorRecordScope } from '../calculatorHistory'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { CalculatorShareDialog } from '../components/CalculatorShareDialog'
import {
  type JdProductCatalog,
  type JdProductCatalogRecord,
} from '../platform'
import { applyJdCpsCouponToPromotion, computeJdCashRecovery, computeJdPromotion, DEFAULT_JD_PROMOTION_ORDER, jdCalculatorDefaultsForSku, jdDefaultPromotionInputs, jdProductMasterContext, jdPromotionOrderFromGroups, normalizeJdPromotionGroups, type JdCalculatorDefaults, type JdPromotionBearer, type JdPromotionGroup, type JdPromotionKey, type JdPromotionLayer } from '../jdProfitCalculator'
import { internalProductCost } from '../productMaster'
import {
  afterSalesDefaultsFor,
  computeAfterSalesAdjusted,
  computePinduoduoUnitProfit,
  jdCpsCouponDiscount,
  simulateJdAdvertisingChannels,
  type AfterSalesInputs,
  type CalculatorInputs,
} from '../pinduoduoProfitCalculator'
import { money } from '../utils'
import type { ErpInventory, InternalProductInfo, ProductMaster } from '../types'

const JD_STORE = 'jd-588485'
const BOOTSTRAP_URL = '/api/calculators/jd/bootstrap'
const RECORDS_URL = '/api/calculators/jd/records'
const RECORDS_API = RECORDS_URL

interface PromotionSnapshot {
  basePrice: string
  order: JdPromotionKey[]
  groups?: JdPromotionGroup[]
  plusEnabled: boolean
  plusZhe: string
  plusBearer: JdPromotionBearer
  plusBearerSharePercent: string
  couponEnabled: boolean
  couponThreshold: string
  couponAmount: string
  couponBearer: JdPromotionBearer
  crossEnabled: boolean
  crossThreshold: string
  crossAmount: string
  crossProportional: boolean
  crossBearer: JdPromotionBearer
  crossBearerSharePercent: string
  activityEnabled: boolean
  activityZhe: string
  activityBearer: JdPromotionBearer
  activityBearerSharePercent: string
  subsidyEnabled: boolean
  subsidyZhe: string
  subsidyBearer: JdPromotionBearer
}

interface CalculatorRecord {
  id: string
  code?: string
  savedAt: string
  store?: string
  variantId: string
  productId: string
  productName: string
  variantAttributes: string
  merchantVariantCode: string
  previewImage?: string
  note: string
  inputs: CalculatorInputs
  afterSales?: AfterSalesInputs
  adSpendText?: string
  cpsRateText?: string
  cpsCouponAmountText?: string
  promotion?: PromotionSnapshot
  appliedTakeHome?: number
  createdBy?: string
  createdByName?: string
  isMine?: boolean
  accessLevel?: 'owner' | 'read' | 'edit'
  canEdit?: boolean
  canShare?: boolean
  canDelete?: boolean
  canCopy?: boolean
  revision?: number
  lastEditedBy?: string
  lastEditedByName?: string
  updatedAt?: number
  shareCount?: number
}

interface SkuSalesSummary {
  unitPrice: number | null
  soldItemCount: number
}

interface JdCalculatorBootstrap {
  catalog: JdProductCatalog
  productMaster: ProductMaster
  internalProductInfo: InternalProductInfo
  erpInventory: ErpInventory
  salesBySku: Record<string, SkuSalesSummary>
  records: CalculatorRecord[]
}

const savedAtText = (value: string) => value.replace('T', ' ').slice(0, 16)
const percent = (value: number | null) => value === null ? '—' : `${(value * 100).toFixed(1)}%`
const roi = (value: number | null) => value === null ? '—' : value.toFixed(2)
const signedMoney = (value: number) => `${value >= 0 ? '＋' : '−'}${money(Math.abs(value))}`

const fulfillmentFields: Array<{ key: keyof CalculatorInputs; label: string; unit: string; hint?: string }> = [
  { key: 'productCost', label: '产品成本', unit: '元' },
  { key: 'packagingCost', label: '包装费', unit: '元' },
  { key: 'giftCost', label: '赠品成本', unit: '元' },
  { key: 'shippingInsuranceCost', label: '运费险', unit: '元' },
  { key: 'shippingCost', label: '运费', unit: '元' },
]

const rateFields: Array<{ key: keyof CalculatorInputs; label: string; unit: string; hint?: string }> = [
  { key: 'platformFeeRatePercent', label: '平台扣点', unit: '%' },
  { key: 'taxRatePercent', label: '税点', unit: '%' },
  { key: 'rebateRatePercent', label: '返点率', unit: '%', hint: '按产品成本计返点' },
]

const inputFieldKeys: Array<keyof CalculatorInputs> = [
  'price', 'productCost', 'packagingCost', 'giftCost', 'shippingInsuranceCost', 'shippingCost',
  'platformFeeRatePercent', 'taxRatePercent', 'rebateRatePercent',
]

const afterSalesFields: Array<{ key: keyof AfterSalesInputs; label: string; unit: string; hint?: string }> = [
  { key: 'secondReturnRatePercent', label: '预计二退率', unit: '%', hint: '京东暂无二退率经营事实，可参考SKU退款率' },
  { key: 'returnShippingCost', label: '往返运费', unit: '元', hint: '默认发货运费×2' },
  { key: 'packagingLossCost', label: '包装损耗', unit: '元' },
  { key: 'damageRatePercent', label: '货损率', unit: '%', hint: '按产品成本计预期损坏' },
]

const bearerTextFor = (layer: JdPromotionLayer) => {
  const ratio = layer.discount > 0 ? layer.merchantBorne / layer.discount : 0
  return ratio >= 0.999 ? '商家承担' : ratio <= 0.001 ? '平台承担' : `商家承担${Math.round(ratio * 100)}%`
}

const PROMOTION_NAMES: Record<JdPromotionKey, string> = {
  plus: 'PLUS会员折扣',
  coupon: '店铺优惠券',
  cross: '跨店满减券',
  activity: '折扣活动',
  subsidy: '政府补贴',
}

const companyFactsFor = (
  records: Array<{ generalAgentPrice: number | null; provincialAgentPrice: number | null; ecommercePrice: number | null; brandSpecialInitialPrice: number | null; specialPrice0225: number | null; specialPrice0305: number | null }>,
  erpAvailable: number | null | undefined,
) => {
  const firstPositive = (values: Array<number | null>) =>
    values.find((value) => typeof value === 'number' && value > 0) ?? null
  const resolution = internalProductCost(records as never)
  return {
    generalAgentPrice: resolution.generalAgentPrice ?? firstPositive(records.map((r) => r.generalAgentPrice)),
    brandSpecialPrice: resolution.brandSpecialPrice,
    brandSpecialConflict: resolution.issue === 'special-price-conflict',
    provincialAgentPrice: firstPositive(records.map((r) => r.provincialAgentPrice)),
    ecommercePrice: firstPositive(records.map((r) => r.ecommercePrice)),
    erpAvailable: typeof erpAvailable === 'number' ? erpAvailable : null,
    hasCompanyRecord: records.length > 0,
  }
}

export function JdProfitCalculatorPage() {
  const auth = useAuth()
  const userId = auth.user?.id || ''
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedSkuId = searchParams.get('sku')?.trim() || ''
  const requestedRecordId = searchParams.get('record')?.trim() || ''
  const autoSelectedSkuId = useRef('')
  const pendingSkuParam = useRef('')
  const [bootstrap, setBootstrap] = useState<JdCalculatorBootstrap | null>(() => peekCalculatorBootstrap(userId, 'jd'))
  const [dataError, setDataError] = useState('')
  const catalog = { data: bootstrap?.catalog ?? null, loading: !bootstrap && !dataError, error: dataError }
  const internalProducts = { data: bootstrap?.internalProductInfo ?? null, loading: !bootstrap && !dataError, error: dataError }
  const erpInventory = { data: bootstrap?.erpInventory ?? null, loading: !bootstrap && !dataError, error: dataError }
  const [productMaster, setProductMaster] = useState<ProductMaster | null>(null)
  const [salesBySku, setSalesBySku] = useState<Map<string, SkuSalesSummary> | null>(null)

  const [query, setQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const searchPopRef = useRef<HTMLDivElement | null>(null)
  const [selected, setSelected] = useState<JdProductCatalogRecord | null>(null)
  const [defaults, setDefaults] = useState<JdCalculatorDefaults | null>(null)
  const [inputs, setInputs] = useState<CalculatorInputs | null>(null)
  const [afterSalesInputs, setAfterSalesInputs] = useState<AfterSalesInputs | null>(null)
  const [adSpendText, setAdSpendText] = useState('')
  const [cpsRateText, setCpsRateText] = useState('')
  const [cpsCouponAmountText, setCpsCouponAmountText] = useState('')
  const [promoForm, setPromoForm] = useState<PromotionSnapshot | null>(null)
  const [appliedTakeHome, setAppliedTakeHome] = useState<number | null>(null)
  const [draggedPromotion, setDraggedPromotion] = useState<JdPromotionKey | null>(null)
  const [promotionDropTarget, setPromotionDropTarget] = useState<{ key: JdPromotionKey; position: 'before' | 'after' } | null>(null)
  const promotionDragRef = useRef<{ key: JdPromotionKey; startX: number; startY: number; active: boolean } | null>(null)
  const promotionDropTargetRef = useRef<{ key: JdPromotionKey; position: 'before' | 'after' } | null>(null)

  const promoFormFromBase = (basePrice: number): PromotionSnapshot => {
    const defaults = jdDefaultPromotionInputs(basePrice)
    return {
      basePrice: String(Math.round(basePrice * 100) / 100),
      order: [...DEFAULT_JD_PROMOTION_ORDER],
      groups: DEFAULT_JD_PROMOTION_ORDER.map((key) => [key]),
      plusEnabled: defaults.plusEnabled ?? true,
      plusZhe: String(defaults.plusZhe),
      plusBearer: defaults.plusBearer,
      plusBearerSharePercent: String(defaults.plusBearerSharePercent),
      couponEnabled: defaults.couponEnabled ?? true,
      couponThreshold: String(defaults.couponThreshold),
      couponAmount: String(defaults.couponAmount),
      couponBearer: defaults.couponBearer,
      crossEnabled: defaults.crossEnabled ?? false,
      crossThreshold: '',
      crossAmount: '',
      crossProportional: defaults.crossProportional ?? false,
      crossBearer: defaults.crossBearer,
      crossBearerSharePercent: String(defaults.crossBearerSharePercent),
      activityEnabled: defaults.activityEnabled ?? false,
      activityZhe: String(defaults.activityZhe),
      activityBearer: defaults.activityBearer,
      activityBearerSharePercent: String(defaults.activityBearerSharePercent),
      subsidyEnabled: defaults.subsidyEnabled ?? false,
      subsidyZhe: String(defaults.subsidyZhe),
      subsidyBearer: defaults.subsidyBearer,
    }
  }
  const [records, setRecords] = useState<CalculatorRecord[]>([])
  const [recordsError, setRecordsError] = useState('')
  const [recordsBusy, setRecordsBusy] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [loadedRecordId, setLoadedRecordId] = useState<string | null>(null)
  const [copiedCode, setCopiedCode] = useState('')
  const [recordNoteText, setRecordNoteText] = useState('')
  const [recordScope, setRecordScope] = useState<CalculatorRecordScope>('mine')
  const [shareRecord, setShareRecord] = useState<CalculatorRecord | null>(null)
  const [conflictRevision, setConflictRevision] = useState<number | null>(null)

  const copyRecordCode = (code: string) => {
    void navigator.clipboard?.writeText(code).catch(() => {})
    setCopiedCode(code)
    window.setTimeout(() => setCopiedCode((current) => current === code ? '' : current), 1500)
  }

  const refreshRecords = async () => {
    const response = await fetch(RECORDS_URL, { cache: 'no-store' })
    const payload = await response.json() as { error?: string; records?: CalculatorRecord[] }
    if (!response.ok) throw new Error(payload.error || `历史测算加载失败：${response.status}`)
    const next = payload.records || []
    setRecords(next)
    return next
  }

  useEffect(() => {
    let active = true
    loadCalculatorBootstrap<JdCalculatorBootstrap>(userId, 'jd', BOOTSTRAP_URL).then((payload) => {
      if (!active) return
      setBootstrap(payload)
      setProductMaster(payload.productMaster)
      setSalesBySku(new Map(Object.entries(payload.salesBySku || {})))
      setRecords(payload.records || [])
    }).catch((reason) => {
      if (active) setDataError(reason instanceof Error ? reason.message : String(reason))
    })
    refreshRecords()
      .then(() => undefined)
      .catch((reason) => { if (active) setRecordsError(reason instanceof Error ? reason.message : String(reason)) })
    return () => { active = false }
  }, [userId])

  // 切换商品浮层：点击外部关闭
  useEffect(() => {
    if (!searchOpen) return
    const onPointerDown = (event: MouseEvent) => {
      if (searchPopRef.current && !searchPopRef.current.contains(event.target as Node)) setSearchOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [searchOpen])

  const companyRecordsByCode = useMemo(() => {
    const map = new Map<string, NonNullable<typeof internalProducts.data>['records']>()
    for (const record of internalProducts.data?.records || []) {
      const code = record.productCode.trim()
      if (!code) continue
      const list = map.get(code) || []
      list.push(record)
      map.set(code, list)
    }
    return map
  }, [internalProducts.data])

  const erpByCode = useMemo(() => new Map(
    (erpInventory.data?.records || []).map((record) => [String(record.productCode).trim(), record.available]),
  ), [erpInventory.data])

  const masterContext = useMemo(
    () => productMaster ? jdProductMasterContext(productMaster) : null,
    [productMaster],
  )

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    if (!keyword || !catalog.data) return []
    return catalog.data.records.filter((record) =>
      record.productName.toLowerCase().includes(keyword)
      || record.salesAttribute.toLowerCase().includes(keyword)
      || record.jdSkuId.includes(keyword)
      || record.merchantSku.toLowerCase().includes(keyword)
      || record.spuId.includes(keyword),
    ).slice(0, 30)
  }, [catalog.data, query])

  // 提交前先拉取服务端最新记录并在其上合并本次改动，避免陈旧标签页整体覆盖丢数据
  const persistRecords = async (op: { type: 'upsert'; record: CalculatorRecord } | { type: 'delete'; id: string }) => {
    setRecordsBusy(true)
    setRecordsError('')
    try {
      let base = records.filter((record) => record.isMine)
      try {
        const fresh = await fetch(RECORDS_URL, { cache: 'no-store' })
        if (fresh.ok) base = (((await fresh.json()) as { records?: CalculatorRecord[] }).records || [])
          .filter((record) => record.isMine)
      } catch { /* 拉取失败时退回本地状态 */ }
      const next = op.type === 'delete'
        ? base.filter((record) => record.id !== op.id)
        : base.some((record) => record.id === op.record.id)
          ? base.map((record) => record.id === op.record.id ? op.record : record)
          : [op.record, ...base].slice(0, 200)
      const response = await authFetch(RECORDS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ records: next }),
      })
      const payload = await response.json() as { error?: string; code?: string; currentRevision?: number; records?: CalculatorRecord[] }
      if (!response.ok) {
        if (response.status === 409 && payload.code === 'calculator_conflict') setConflictRevision(payload.currentRevision || null)
        throw new Error(payload.error || '测算记录保存失败')
      }
      setConflictRevision(null)
      setRecords(payload.records || next)
      setRecordScope('mine')
      return true
    } catch (reason) {
      setRecordsError(reason instanceof Error ? reason.message : String(reason))
      return false
    } finally {
      setRecordsBusy(false)
    }
  }

  const updateSharedRecord = async (record: CalculatorRecord, expectedRevision: number) => {
    setRecordsBusy(true)
    setRecordsError('')
    try {
      const response = await authFetch(`${RECORDS_API}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ record, expectedRevision }),
      })
      const payload = await response.json() as { error?: string; code?: string; currentRevision?: number; record?: CalculatorRecord }
      if (!response.ok || !payload.record) {
        if (response.status === 409 && payload.code === 'calculator_conflict') setConflictRevision(payload.currentRevision || null)
        throw new Error(payload.error || '共享测算保存失败')
      }
      setConflictRevision(null)
      setRecords((current) => current.map((item) => item.id === payload.record!.id ? payload.record! : item))
      return true
    } catch (reason) {
      setRecordsError(reason instanceof Error ? reason.message : String(reason))
      return false
    } finally {
      setRecordsBusy(false)
    }
  }

  // 选中后把 ?sku= 写入 URL，让详情/列表切换由 URL 驱动；导航更新在 startTransition 中晚于本地 state 生效，需用 pendingSkuParam 防止清空 effect 误判
  const syncSkuParam = (skuId: string, keepRecord = false) => {
    autoSelectedSkuId.current = skuId
    pendingSkuParam.current = skuId
    if (requestedSkuId !== skuId || (!keepRecord && requestedRecordId)) {
      setSearchParams((params) => {
        const next = new URLSearchParams(params)
        next.set('sku', skuId)
        if (!keepRecord) next.delete('record')
        return next
      })
    }
  }

  const selectSku = (record: JdProductCatalogRecord, options?: { keepRecord?: boolean }) => {
    if (!masterContext) return
    const today = new Date().toISOString().slice(0, 10)
    const sales = salesBySku?.get(record.jdSkuId)
    const nextDefaults = jdCalculatorDefaultsForSku(
      sales?.unitPrice ?? 0,
      masterContext.records.get(record.jdSkuId),
      masterContext.defaults,
      companyRecordsByCode.get(record.merchantSku.trim()) || [],
      today,
    )
    setSelected(record)
    setDefaults(nextDefaults)
    setInputs({ ...nextDefaults.inputs })
    setAfterSalesInputs(afterSalesDefaultsFor(nextDefaults.inputs, 0))
    setPromoForm(promoFormFromBase(nextDefaults.inputs.price))
    setAppliedTakeHome(null)
    setAdSpendText('')
    setCpsRateText('')
    setCpsCouponAmountText('')
    setQuery('')
    setSearchOpen(false)
    setSaveMessage('')
    setLoadedRecordId(null)
    setRecordNoteText('')
    syncSkuParam(record.jdSkuId, options?.keepRecord)
  }

  useEffect(() => {
    if (!masterContext || !requestedSkuId || autoSelectedSkuId.current === requestedSkuId || !catalog.data) return
    const record = catalog.data.records.find((item) => item.jdSkuId === requestedSkuId)
    if (!record) return
    autoSelectedSkuId.current = requestedSkuId
    selectSku(record)
  }, [catalog.data, masterContext, requestedSkuId, salesBySku])

  // 点击左侧菜单回到无 ?sku= 的同路由时，清空选中状态恢复默认列表视图
  useEffect(() => {
    if (requestedSkuId === pendingSkuParam.current) pendingSkuParam.current = ''
    if (requestedSkuId || !selected) return
    if (pendingSkuParam.current) return
    autoSelectedSkuId.current = ''
    setSelected(null)
    setDefaults(null)
    setInputs(null)
    setAfterSalesInputs(null)
    setAdSpendText('')
    setCpsRateText('')
    setCpsCouponAmountText('')
    setPromoForm(null)
    setAppliedTakeHome(null)
    setQuery('')
    setSearchOpen(false)
    setLoadedRecordId(null)
    setSaveMessage('')
    setRecordNoteText('')
  }, [requestedSkuId, selected])

  const saveCurrent = async (mode: 'overwrite' | 'new') => {
    if (!selected || !inputs) return
    setSaveMessage('')
    const existing = mode === 'overwrite' && loadedRecordId
      ? records.find((record) => record.id === loadedRecordId && record.canEdit)
      : undefined
    const record: CalculatorRecord = {
      id: existing?.id ?? `calc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      ...(existing?.code ? { code: existing.code } : {}),
      ...(existing?.revision ? { revision: existing.revision } : {}),
      savedAt: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 19),
      store: JD_STORE,
      variantId: selected.jdSkuId,
      productId: selected.spuId,
      productName: selected.productName,
      variantAttributes: selected.salesAttribute,
      merchantVariantCode: selected.merchantSku,
      previewImage: selected.imagePath ? `/${selected.imagePath}` : '',
      note: recordNoteText.trim(),
      inputs: { ...inputs },
      ...(afterSalesInputs ? { afterSales: { ...afterSalesInputs } } : {}),
      adSpendText,
      cpsRateText,
      cpsCouponAmountText,
      ...(promoForm ? { promotion: { ...promoForm } } : {}),
      ...(appliedTakeHome !== null ? { appliedTakeHome } : {}),
    }
    const saved = existing && !existing.isMine
      ? await updateSharedRecord(record, existing.revision || 0)
      : await persistRecords({ type: 'upsert', record })
    if (saved) {
      setLoadedRecordId(record.id)
      const displayName = record.note || selected.productName
      setSaveMessage(existing
        ? existing.isMine
          ? `已覆盖保存测算记录「${displayName}」。`
          : `已保存对共享测算「${displayName}」的修改。`
        : mode === 'new' && loadedRecordId
          ? `已另存为新的测算记录「${displayName}」。`
          : `已保存测算记录「${displayName}」。`)
    }
  }

  const deleteRecord = (id: string) => {
    if (id === loadedRecordId) setLoadedRecordId(null)
    void persistRecords({ type: 'delete', id })
  }

  // 恢复记录保存时的完整参数快照；旧记录缺少快照字段时退回默认值
  const applyRecordSnapshot = (record: CalculatorRecord) => {
    setInputs({ ...record.inputs })
    setAfterSalesInputs(record.afterSales ? { ...record.afterSales } : afterSalesDefaultsFor(record.inputs, 0))
    // 与默认值合并，兼容缺少新增促销字段的旧快照；缺勾选开关的旧快照按当时的值反推参与状态
    const promoBase = promoFormFromBase(record.inputs.price)
    if (record.promotion) {
      const restored: PromotionSnapshot = { ...promoBase, ...record.promotion }
      restored.groups = normalizeJdPromotionGroups(record.promotion.groups, record.promotion.order)
      restored.order = jdPromotionOrderFromGroups(restored.groups)
      // 旧记录保存时跨店满减只有按比例分摊算法，缺少新字段时继续保持原结果。
      if (typeof (record.promotion as Partial<PromotionSnapshot>).crossProportional !== 'boolean') {
        restored.crossProportional = true
      }
      // 旧记录折扣活动只有「商家承担 %」，迁移为承担方=商家 + 承担比例
      const legacyActivityShare = (record.promotion as { activityMerchantSharePercent?: string }).activityMerchantSharePercent
      if (typeof legacyActivityShare === 'string' && typeof (record.promotion as Partial<PromotionSnapshot>).activityBearerSharePercent !== 'string') {
        restored.activityBearer = 'merchant'
        restored.activityBearerSharePercent = legacyActivityShare
      }
      if (typeof record.promotion.plusEnabled !== 'boolean') {
        const num = (value: string) => { const parsed = Number(value); return Number.isFinite(parsed) ? parsed : 0 }
        restored.plusEnabled = num(restored.plusZhe) > 0 && num(restored.plusZhe) < 10
        restored.couponEnabled = num(restored.couponThreshold) > 0 && num(restored.couponAmount) > 0
        restored.crossEnabled = num(restored.crossThreshold) > 0 && num(restored.crossAmount) > 0
        restored.activityEnabled = num(restored.activityZhe) > 0 && num(restored.activityZhe) < 10
        restored.subsidyEnabled = num(restored.subsidyZhe) > 0 && num(restored.subsidyZhe) < 10
      }
      setPromoForm(restored)
    } else {
      setPromoForm(promoBase)
    }
    setAppliedTakeHome(record.appliedTakeHome ?? null)
    setAdSpendText(record.adSpendText ?? '')
    setCpsRateText(record.cpsRateText ?? '')
    setCpsCouponAmountText(record.cpsCouponAmountText ?? '')
    setRecordNoteText(record.note ?? '')
    setLoadedRecordId(record.id)
  }

  const loadRecord = (record: CalculatorRecord) => {
    setSaveMessage('')
    setConflictRevision(null)
    const sku = catalog.data?.records.find((item) => item.jdSkuId === record.variantId)
    if (sku) {
      selectSku(sku, { keepRecord: true })
      applyRecordSnapshot(record)
      return
    }
    setSelected({
      jdSkuId: record.variantId,
      spuId: record.productId,
      merchantSku: record.merchantVariantCode,
      productName: record.productName,
      salesAttribute: record.variantAttributes,
      shortTitle: '',
      productUrl: '',
      imagePath: record.previewImage?.replace(/^\//, '') || '',
    })
    setDefaults({
      inputs: { ...record.inputs },
      isAccessory: false,
      isBrandSpecial: false,
      hasProductCost: true,
      hasStoreCost: true,
      costIssue: null,
      storeCostEffectiveDate: '',
      shippingProviderName: '',
      fulfillmentModeLabel: '',
    })
    applyRecordSnapshot(record)
    setQuery('')
    setSearchOpen(false)
    syncSkuParam(record.variantId, true)
  }

  useEffect(() => {
    if (!requestedRecordId || loadedRecordId === requestedRecordId) return
    if (!catalog.data || !masterContext) return
    const record = records.find((item) => item.id === requestedRecordId)
    if (record) loadRecord(record)
  }, [catalog.data, masterContext, records, requestedRecordId, loadedRecordId])

  const updateInput = (key: keyof CalculatorInputs, raw: string) => {
    if (!inputs) return
    const value = raw === '' ? 0 : Number(raw)
    if (!Number.isFinite(value) || value < 0) return
    if (key === 'price') setAppliedTakeHome(null)
    setInputs({ ...inputs, [key]: value })
  }

  const updatePromoBearer = (key: 'plusBearer' | 'couponBearer' | 'crossBearer' | 'activityBearer' | 'subsidyBearer', value: JdPromotionBearer) => {
    if (!promoForm) return
    setPromoForm({ ...promoForm, [key]: value })
  }

  const updatePromoEnabled = (key: 'plusEnabled' | 'couponEnabled' | 'crossEnabled' | 'activityEnabled' | 'subsidyEnabled', value: boolean) => {
    if (!promoForm) return
    setPromoForm({ ...promoForm, [key]: value })
  }

  const updatePromoText = (key: 'basePrice' | 'plusZhe' | 'plusBearerSharePercent' | 'couponThreshold' | 'couponAmount' | 'crossThreshold' | 'crossAmount' | 'crossBearerSharePercent' | 'activityZhe' | 'activityBearerSharePercent' | 'subsidyZhe', raw: string) => {
    if (!promoForm) return
    if (!/^\d{0,7}(\.\d{0,2})?$/.test(raw)) return
    setPromoForm({ ...promoForm, [key]: raw })
  }

  const reorderPromotion = (dragged: JdPromotionKey, target: JdPromotionKey, position: 'before' | 'after') => {
    setPromoForm((current) => {
      if (!current || dragged === target) return current
      const groups = normalizeJdPromotionGroups(current.groups, current.order)
        .map((group) => group.filter((key) => key !== dragged))
        .filter((group) => group.length > 0)
      const targetGroupIndex = groups.findIndex((group) => group.includes(target))
      if (targetGroupIndex < 0) return current
      groups.splice(targetGroupIndex + (position === 'after' ? 1 : 0), 0, [dragged])
      return { ...current, groups, order: jdPromotionOrderFromGroups(groups) }
    })
  }

  const togglePromotionParallel = (key: JdPromotionKey) => {
    setPromoForm((current) => {
      if (!current) return current
      const groups = normalizeJdPromotionGroups(current.groups, current.order).map((group) => [...group])
      const groupIndex = groups.findIndex((group) => group.includes(key))
      if (groupIndex < 0) return current
      const memberIndex = groups[groupIndex].indexOf(key)
      if (memberIndex > 0) {
        const before = groups[groupIndex].slice(0, memberIndex)
        const after = groups[groupIndex].slice(memberIndex)
        groups.splice(groupIndex, 1, before, after)
      } else {
        if (groupIndex === 0) return current
        groups.splice(groupIndex - 1, 2, [...groups[groupIndex - 1], ...groups[groupIndex]])
      }
      return { ...current, groups, order: jdPromotionOrderFromGroups(groups) }
    })
  }

  const startPromotionDrag = (key: JdPromotionKey, event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    promotionDragRef.current = { key, startX: event.clientX, startY: event.clientY, active: false }

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const drag = promotionDragRef.current
      if (!drag) return
      if (!drag.active) {
        if (Math.hypot(moveEvent.clientX - drag.startX, moveEvent.clientY - drag.startY) < 5) return
        drag.active = true
        setDraggedPromotion(drag.key)
        document.body.classList.add('is-dragging-promotion')
      }
      const row = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY)?.closest<HTMLElement>('[data-promotion-key]')
      const target = row?.dataset.promotionKey as JdPromotionKey | undefined
      if (!row || !target || target === drag.key) {
        promotionDropTargetRef.current = null
        setPromotionDropTarget(null)
        return
      }
      const bounds = row.getBoundingClientRect()
      const position = moveEvent.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after'
      const targetGroup = normalizeJdPromotionGroups(promoForm?.groups, promoForm?.order)
        .find((group) => group.includes(target)) || [target]
      const anchor = position === 'before' ? targetGroup[0] : targetGroup[targetGroup.length - 1]
      const nextTarget = { key: anchor, position } as const
      promotionDropTargetRef.current = nextTarget
      setPromotionDropTarget(nextTarget)
    }

    const finishPromotionDrag = () => {
      const drag = promotionDragRef.current
      const target = promotionDropTargetRef.current
      if (drag?.active && target) reorderPromotion(drag.key, target.key, target.position)
      promotionDragRef.current = null
      promotionDropTargetRef.current = null
      setDraggedPromotion(null)
      setPromotionDropTarget(null)
      document.body.classList.remove('is-dragging-promotion')
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', finishPromotionDrag)
      document.removeEventListener('pointercancel', finishPromotionDrag)
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', finishPromotionDrag)
    document.addEventListener('pointercancel', finishPromotionDrag)
  }

  const promoNumber = (raw: string, maximum: number) => {
    const value = raw === '' ? 0 : Number(raw)
    return Number.isFinite(value) ? Math.min(value, maximum) : 0
  }

  // 折数容错：输入 95 视为 9.5 折（10 < 值 ≤ 100 时除以 10），最终限制在 0–10；非法输入视为不打折
  const promoZhe = (raw: string) => {
    const value = Number(raw)
    if (raw.trim() === '' || !Number.isFinite(value)) return 10
    const zhe = value > 10 && value <= 100 ? value / 10 : value
    return Math.min(Math.max(zhe, 0), 10)
  }

  const updateAfterSalesInput = (key: keyof AfterSalesInputs, raw: string) => {
    if (!afterSalesInputs) return
    const value = raw === '' ? 0 : Number(raw)
    if (!Number.isFinite(value) || value < 0) return
    setAfterSalesInputs({ ...afterSalesInputs, [key]: value })
  }

  const { mine: ownRecords, shared: sharedRecords } = useMemo(
    () => partitionCalculatorRecords(records.filter((record) => record.store === JD_STORE)),
    [records],
  )
  const visibleRecords = recordScope === 'mine' ? ownRecords : sharedRecords
  const codeQuery = query.trim().toUpperCase()
  const noteQuery = query.trim().toLowerCase()
  const recordMatches = noteQuery
    ? visibleRecords.filter((record) => (
      (record.code || '').toUpperCase() === codeQuery
      || (record.note || '').toLowerCase().includes(noteQuery)
    )).slice(0, 20)
    : []
  const promotion = promoForm ? computeJdPromotion({
    basePrice: promoNumber(promoForm.basePrice, 1000000),
    order: promoForm.order,
    groups: promoForm.groups,
    plusEnabled: promoForm.plusEnabled,
    couponEnabled: promoForm.couponEnabled,
    crossEnabled: promoForm.crossEnabled,
    activityEnabled: promoForm.activityEnabled,
    subsidyEnabled: promoForm.subsidyEnabled,
    plusZhe: promoZhe(promoForm.plusZhe),
    plusBearer: promoForm.plusBearer,
    plusBearerSharePercent: promoNumber(promoForm.plusBearerSharePercent, 100),
    couponThreshold: promoNumber(promoForm.couponThreshold, 1000000),
    couponAmount: promoNumber(promoForm.couponAmount, 1000000),
    couponBearer: promoForm.couponBearer,
    crossThreshold: promoNumber(promoForm.crossThreshold, 1000000),
    crossAmount: promoNumber(promoForm.crossAmount, 1000000),
    crossProportional: promoForm.crossProportional,
    crossBearer: promoForm.crossBearer,
    crossBearerSharePercent: promoNumber(promoForm.crossBearerSharePercent, 100),
    activityZhe: promoZhe(promoForm.activityZhe),
    activityBearer: promoForm.activityBearer,
    activityBearerSharePercent: promoNumber(promoForm.activityBearerSharePercent, 100),
    subsidyZhe: promoZhe(promoForm.subsidyZhe),
    subsidyBearer: promoForm.subsidyBearer,
  }) : null
  const applyPromotion = () => {
    if (!inputs || !promotion) return
    setInputs({ ...inputs, price: Math.round(promotion.merchantReceivable * 100) / 100 })
    setAppliedTakeHome(Math.round(promotion.takeHomePrice * 100) / 100)
  }
  const result = inputs ? computePinduoduoUnitProfit(inputs) : null
  const afterSalesResult = result && inputs && afterSalesInputs
    ? computeAfterSalesAdjusted(result, inputs, afterSalesInputs)
    : null
  const adSpend = adSpendText === '' ? 0 : Number(adSpendText)
  const cpsRate = cpsRateText === '' ? 0 : Number(cpsRateText)
  const cpsCouponAmount = cpsCouponAmountText === '' ? 0 : Number(cpsCouponAmountText)
  const promotionCpsOutcome = promotion
    ? applyJdCpsCouponToPromotion(promotion, cpsCouponAmount)
    : null
  const cpsCustomerPrice = inputs ? (appliedTakeHome ?? inputs.price) : 0
  const cpsCouponDiscount = jdCpsCouponDiscount(cpsCustomerPrice, cpsCouponAmount)
  const cpsInputs = inputs
    ? { ...inputs, price: Math.max(inputs.price - cpsCouponDiscount, 0) }
    : null
  const cpsUnitResult = cpsInputs ? computePinduoduoUnitProfit(cpsInputs) : null
  const cpsAfterSalesResult = cpsUnitResult && cpsInputs && afterSalesInputs
    ? computeAfterSalesAdjusted(cpsUnitResult, cpsInputs, afterSalesInputs)
    : null
  const completedOrderRate = afterSalesResult && inputs && inputs.price > 0
    ? afterSalesResult.expectedRevenuePerOrdered / inputs.price
    : 0
  const cpsBasePerOrdered = completedOrderRate * Math.max(cpsCustomerPrice - cpsCouponDiscount, 0)
  const adSimulation = afterSalesResult
    && cpsAfterSalesResult
    && cpsUnitResult
    && Number.isFinite(adSpend) && adSpend >= 0
    && Number.isFinite(cpsRate) && cpsRate >= 0
    && Number.isFinite(cpsCouponAmount) && cpsCouponAmount >= 0
    ? simulateJdAdvertisingChannels(
        afterSalesResult,
        adSpend,
        cpsRate,
        cpsBasePerOrdered,
        cpsAfterSalesResult.expectedProfitPerOrdered,
        Math.max(cpsCustomerPrice - cpsCouponDiscount, 0),
        cpsUnitResult.profitWithRebate,
      )
    : null
  const cpsExpectedProfitForegonePerOrdered = adSimulation && afterSalesInputs
    ? adSimulation.cpsProfitPerCompleted * afterSalesInputs.secondReturnRatePercent / 100
    : null
  const edited = defaults && inputs
    ? inputFieldKeys.some((key) => inputs[key] !== defaults.inputs[key])
    : false
  const loadedRecord = loadedRecordId === null ? undefined : records.find((record) => record.id === loadedRecordId)
  const hasLoadedRecord = loadedRecord !== undefined
  const hasLoadedOwnRecord = loadedRecord?.isMine === true
  const hasLoadedEditableRecord = loadedRecord?.canEdit === true
  const selectedSales = selected ? salesBySku?.get(selected.jdSkuId) : undefined
  const promotionApplied = promotion !== null && inputs !== null && appliedTakeHome !== null
    && inputs.price === Math.round(promotion.merchantReceivable * 100) / 100
  const layerByName = useMemo(() => {
    const map = new Map<string, JdPromotionLayer>()
    for (const layer of promotion?.layers || []) map.set(layer.name, layer)
    return map
  }, [promotion])

  const loading = catalog.loading || internalProducts.loading || (!productMaster && !dataError)
  const error = catalog.error || internalProducts.error || dataError

  if (loading) return <div className="loading-state">正在加载京东利润计算器数据…</div>
  if (error) return <div className="error-state"><h1>利润计算器不可用</h1><p>{error}</p></div>

  const facts = selected
    ? companyFactsFor(companyRecordsByCode.get(selected.merchantSku.trim()) || [], erpByCode.get(selected.merchantSku.trim()))
    : null
  const inventoryQuantity = typeof facts?.erpAvailable === 'number' && facts.erpAvailable >= 0
    ? facts.erpAvailable
    : null
  const directAdSpendForRecovery = Number.isFinite(adSpend) && adSpend >= 0 ? adSpend : 0
  const cpsRateForRecovery = Number.isFinite(cpsRate) && cpsRate >= 0 ? cpsRate : 0
  const cpsCommissionPerCompletedForRecovery = Math.max(cpsCustomerPrice - cpsCouponDiscount, 0)
    * cpsRateForRecovery / 100
  const cashRecovery = cpsUnitResult && cpsInputs && cpsAfterSalesResult && afterSalesInputs
    ? computeJdCashRecovery(
      cpsUnitResult,
      cpsInputs,
      cpsAfterSalesResult,
      afterSalesInputs,
      {
        directAdSpendPerOrdered: directAdSpendForRecovery,
        cpsCommissionPerCompleted: cpsCommissionPerCompletedForRecovery,
      },
      inventoryQuantity,
    )
    : null
  const hasRecoveryAdvertisingInput = adSpendText.trim() !== ''
    || cpsRateText.trim() !== ''
    || cpsCouponAmountText.trim() !== ''
  const recoveryAdvertisingFormula = [
    adSpendText.trim() !== ''
      ? `直投 ${money(directAdSpendForRecovery)}÷${100 - (afterSalesInputs?.secondReturnRatePercent ?? 0)}%`
      : null,
    cpsRateText.trim() !== ''
      ? `京挑客 ${money(Math.max(cpsCustomerPrice - cpsCouponDiscount, 0))}×${cpsRateForRecovery}%`
      : null,
  ].filter((value): value is string => value !== null).join(' ＋ ')
  const hasCompleteRecoveryCosts = Boolean(defaults?.hasStoreCost)
  const promotionGroups = normalizeJdPromotionGroups(promoForm?.groups, promoForm?.order)
  const promotionOrder = jdPromotionOrderFromGroups(promotionGroups)
  const promotionGroupInfo = (key: JdPromotionKey) => {
    const groupIndex = promotionGroups.findIndex((group) => group.includes(key))
    const group = promotionGroups[groupIndex] || [key]
    const memberIndex = group.indexOf(key)
    const effectiveMembers = group.filter((member) => layerByName.has(PROMOTION_NAMES[member]))
    return {
      group,
      groupIndex,
      memberIndex,
      isParallel: group.length > 1,
      isLastEffective: effectiveMembers.at(-1) === key,
    }
  }

  const chainDelta = (key: JdPromotionKey, enabled: boolean, layer: JdPromotionLayer | undefined, fallback: string) => {
    const groupInfo = promotionGroupInfo(key)
    return (
      <span className="jdc-chain-delta">
        {enabled && layer
          ? <>
              <span className="minus">−{money(layer.discount)}</span>
              <span className="after">{groupInfo.isParallel
                ? groupInfo.isLastEffective
                  ? `组后 ${money(layer.priceAfter)}`
                  : `基数 ${money(layer.groupBasePrice)}`
                : money(layer.priceAfter)}</span>
              <span className="who">{bearerTextFor(layer)}</span>
            </>
          : enabled ? <span className="noop">{fallback}</span> : null}
      </span>
    )
  }

  const chainRowClass = (enabled: boolean, layer: JdPromotionLayer | undefined) =>
    `jdc-chain-row${enabled ? (layer ? ' is-on' : '') : ' is-off'}`

  const promotionRowClass = (key: JdPromotionKey, enabled: boolean, layer: JdPromotionLayer | undefined) => {
    const groupInfo = promotionGroupInfo(key)
    const dragging = draggedPromotion === key ? ' is-promotion-dragging' : ''
    const drop = promotionDropTarget?.key === key ? ` is-promotion-drop-${promotionDropTarget.position}` : ''
    const edge = promotionOrder.at(-1) === key ? ' is-promotion-last' : ''
    const parallel = groupInfo.isParallel
      ? ` is-parallel-group is-parallel-${groupInfo.memberIndex === 0 ? 'first' : groupInfo.memberIndex === groupInfo.group.length - 1 ? 'last' : 'middle'}`
      : ''
    return `${chainRowClass(enabled, layer)} is-promotion-sortable${edge}${parallel}${dragging}${drop}`
  }

  const promotionRowStyle = (key: JdPromotionKey) => ({
    order: promotionOrder.indexOf(key) + 1,
  })

  const promotionDragHandle = (key: JdPromotionKey) => (
    <button
      type="button"
      className="jdc-promotion-drag-handle"
      aria-label={`拖动调整${PROMOTION_NAMES[key]}计算顺序`}
      title="按住拖动调整计算顺序"
      onPointerDown={(event) => startPromotionDrag(key, event)}
    >
      <GripVertical size={15} aria-hidden />
    </button>
  )

  const promotionParallelToggle = (key: JdPromotionKey) => {
    if (promotionOrder.indexOf(key) <= 0) return null
    const parallel = promotionGroupInfo(key).memberIndex > 0
    return (
      <button
        type="button"
        className={`jdc-parallel-toggle${parallel ? ' is-active' : ''}`}
        aria-pressed={parallel}
        title={parallel ? '点击取消与上一项并行' : '与上一项共享同一计算基数'}
        onClick={() => togglePromotionParallel(key)}
      >
        <Link2 size={12} aria-hidden /> {parallel ? '并行' : '与上项并行'}
      </button>
    )
  }

  const searchPanel = (
    <>
      <div className="jdc-search">
        <Search aria-hidden />
        <input
          type="text"
          value={query}
          placeholder="搜索商品名称 / 京东SKU / 商家SKU / SPU，或输入计算ID（如 JD-1）…"
          autoFocus={searchOpen}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {query.trim() && (
        <div className="jdc-results">
          {recordMatches.map((record) => (
            <button type="button" key={`record-${record.id}`} className="jdc-result" onClick={() => loadRecord(record)}>
              <span className="jdc-result-code">{record.code}</span>
              <span className="jdc-result-name">
                <strong>{record.note || record.productName}</strong>
                <small>{record.note ? `${record.productName} · ` : ''}历史测算记录 · {record.createdByName || '未知账号'} · 保存于 {savedAtText(record.savedAt)} · 点击载入</small>
              </span>
              <span className="jdc-result-price">{money(record.inputs.price)}</span>
            </button>
          ))}
          {results.length === 0 && recordMatches.length === 0 && <div className="jdc-empty">没有匹配的SKU或计算ID</div>}
          {results.map((record) => {
            const sales = salesBySku?.get(record.jdSkuId)
            return (
              <button type="button" key={record.jdSkuId} className="jdc-result" onClick={() => selectSku(record)}>
                {record.imagePath ? <img src={`/${record.imagePath}`} alt="" loading="lazy" /> : <span className="jdc-bar-noimg">无图</span>}
                <span className="jdc-result-name">
                  <strong>{record.productName}</strong>
                  <small>{record.salesAttribute || '默认规格'} · SKU {record.jdSkuId} · 商家SKU {record.merchantSku || '—'}</small>
                </span>
                <span className="jdc-result-price">{sales?.unitPrice != null ? money(sales.unitPrice) : '—'}</span>
              </button>
            )
          })}
        </div>
      )}
    </>
  )

  return (
    <div className="page-stack pdd-page">
      <div className="page-title-row">
        <div>
          <span className="pdd-platform-label">JD</span>
          <h1>利润计算器</h1>
          <p>选择SKU后按京东当前成本配置测算单件利润与保本ROI；所有参数可临时调整，仅本页测算，不会写回商品成本资料。</p>
        </div>
        <div className="page-title-actions">
          {auth.canAccess(JD_STORE, 'jd.costs') ? <Link className="text-link" to="/jd/costs">店铺成本配置</Link> : null}
          {catalog.data && <DataFreshnessBadge date={catalog.data.generatedAt} label="商品目录" mode="on-demand" />}
        </div>
      </div>

      {!selected && (
        <section className="overview-section">
          <div className="section-heading-row">
            <div><h2>选择商品SKU</h2><p>搜索商品名称、销售属性、京东SKUID、商家SKU或SPU；也可输入计算ID载入历史测算</p></div>
            <Search size={18} aria-hidden />
          </div>
          {searchPanel}
        </section>
      )}

      {selected && defaults && inputs && result && (
        <>
          <div className="jdc-bar">
            {selected.imagePath ? <img src={`/${selected.imagePath}`} alt="" /> : <span className="jdc-bar-noimg">无图</span>}
            <div className="jdc-bar-main">
              <span className="jdc-bar-name" title={selected.productName}>{selected.productName}</span>
              <div className="jdc-bar-meta">
                <span className={`jdc-tag${defaults.isBrandSpecial ? ' is-special' : ' is-blue'}`}>
                  {defaults.isAccessory ? '配件' : defaults.isBrandSpecial ? '品牌特价' : '普通品'}
                </span>
                {defaults.fulfillmentModeLabel && <span className="jdc-tag">{defaults.fulfillmentModeLabel}{defaults.shippingProviderName ? ` · ${defaults.shippingProviderName}` : ''}</span>}
                <span>SKU {selected.jdSkuId}</span>
                <span>商家SKU {selected.merchantSku || '—'}</span>
                {facts && (
                  <>
                    <span>总代价 <b>{facts.generalAgentPrice === null ? '—' : money(facts.generalAgentPrice)}</b></span>
                    {facts.brandSpecialPrice !== null && <span>品牌特价 <b>{facts.brandSpecialConflict ? '冲突' : money(facts.brandSpecialPrice)}</b></span>}
                    <span>电商价 <b>{facts.ecommercePrice === null ? '—' : money(facts.ecommercePrice)}</b></span>
                    <span>ERP库存 <b>{facts.erpAvailable === null ? '—' : facts.erpAvailable.toLocaleString('zh-CN')}</b></span>
                  </>
                )}
                {selectedSales?.unitPrice == null && <span className="jdc-tag is-warning">最近30天无成交，售价默认0，请手动填写</span>}
                {!defaults.hasProductCost && <span className="jdc-tag is-warning">缺公司产品成本，产品成本默认0，请手动填写</span>}
                {defaults.costIssue && <span className="jdc-tag is-warning">公司成本冲突：{defaults.costIssue}</span>}
                {facts && !facts.hasCompanyRecord && <span className="jdc-tag is-warning">未关联公司产品编码</span>}
              </div>
            </div>
            <div className="jdc-bar-kpis">
              <div className="jdc-kpi is-hero"><span>含返点利润</span><strong className={result.profitWithRebate < 0 ? 'is-negative' : ''}>{money(result.profitWithRebate)}</strong></div>
              <div className="jdc-kpi"><span>利润率</span><strong>{percent(result.profitWithRebateRate)}</strong></div>
              <div className="jdc-kpi is-breakeven"><span>保本ROI</span><strong>{roi(result.breakEvenRoi)}</strong></div>
            </div>
            <div className="jdc-switch-wrap" ref={searchPopRef}>
              <button className="jdc-switch-btn" type="button" onClick={() => setSearchOpen((open) => !open)}>
                <ArrowLeftRight size={13} /> 切换商品
              </button>
              {searchOpen && <div className="jdc-pop">{searchPanel}</div>}
            </div>
          </div>

          <div className="jdc-layout">
            <div className="jdc-steps">
              {promoForm && promotion && (
                <section className="jdc-step">
                  <div className="jdc-step-head">
                    <span className="jdc-step-no">1</span>
                    <h2>促销与定价</h2>
                    <span className="jdc-step-sub">拖动调整顺序；点“与上项并行”后组内共享同一价格基数</span>
                    <button type="button" className="jdc-step-action" onClick={() => setPromoForm(promoFormFromBase(defaults.inputs.price))}>
                      <RotateCcw size={13} /> 重置促销
                    </button>
                  </div>
                  <div className="jdc-step-body">
                    <div className="jdc-chain">
                      <div className="jdc-chain-row is-base">
                        <div className="jdc-chain-rail"><span className="jdc-chain-dot" /></div>
                        <div className="jdc-chain-main">
                          <div className="jdc-chain-title-row">
                            <span className="jdc-chain-title">页面价</span>
                            <span className="jdc-chain-summary">促销前挂牌价 · 默认最近30天件单价</span>
                          </div>
                          <div className="jdc-chain-fields">
                            <span className="jdc-cfield"><span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.basePrice} onChange={(event) => updatePromoText('basePrice', event.target.value)} /><small>元</small></span></span>
                          </div>
                        </div>
                        <span className="jdc-chain-delta"><span className="after">{money(promoNumber(promoForm.basePrice, 1000000))}</span></span>
                      </div>

                      <div className={promotionRowClass('plus', promoForm.plusEnabled, layerByName.get('PLUS会员折扣'))} style={promotionRowStyle('plus')} data-promotion-key="plus">
                        <div className="jdc-chain-rail"><span className="jdc-chain-dot" /></div>
                        <div className="jdc-chain-main">
                          <div className="jdc-chain-title-row">
                            {promotionDragHandle('plus')}
                            <label className="jdc-tgl"><input type="checkbox" checked={promoForm.plusEnabled} onChange={(event) => updatePromoEnabled('plusEnabled', event.target.checked)} /><i /></label>
                            <span className="jdc-chain-title">PLUS会员折扣</span>
                            {promotionParallelToggle('plus')}
                            <span className="jdc-chain-summary">一般9.5折</span>
                          </div>
                          <div className="jdc-chain-fields">
                            <span className="jdc-cfield">折数 <span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.plusZhe} placeholder="如 9.5" disabled={!promoForm.plusEnabled} onChange={(event) => updatePromoText('plusZhe', event.target.value)} /><small>折</small></span></span>
                            <span className="jdc-cfield">承担方 <select value={promoForm.plusBearer} disabled={!promoForm.plusEnabled} onChange={(event) => updatePromoBearer('plusBearer', event.target.value as JdPromotionBearer)}>
                              <option value="merchant">商家承担</option>
                              <option value="platform">平台承担</option>
                            </select></span>
                            <span className="jdc-cfield">承担比例 <span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.plusBearerSharePercent} disabled={!promoForm.plusEnabled} onChange={(event) => updatePromoText('plusBearerSharePercent', event.target.value)} /><small>%</small></span></span>
                          </div>
                        </div>
                        {chainDelta('plus', promoForm.plusEnabled, layerByName.get('PLUS会员折扣'), '不生效')}
                      </div>

                      <div className={promotionRowClass('coupon', promoForm.couponEnabled, layerByName.get('店铺优惠券'))} style={promotionRowStyle('coupon')} data-promotion-key="coupon">
                        <div className="jdc-chain-rail"><span className="jdc-chain-dot" /></div>
                        <div className="jdc-chain-main">
                          <div className="jdc-chain-title-row">
                            {promotionDragHandle('coupon')}
                            <label className="jdc-tgl"><input type="checkbox" checked={promoForm.couponEnabled} onChange={(event) => updatePromoEnabled('couponEnabled', event.target.checked)} /><i /></label>
                            <span className="jdc-chain-title">店铺优惠券</span>
                            {promotionParallelToggle('coupon')}
                            <span className="jdc-chain-summary">达到门槛一次性减免，未达门槛不生效</span>
                          </div>
                          <div className="jdc-chain-fields">
                            <span className="jdc-cfield">满 <span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.couponThreshold} placeholder="0" disabled={!promoForm.couponEnabled} onChange={(event) => updatePromoText('couponThreshold', event.target.value)} /><small>元</small></span></span>
                            <span className="jdc-cfield">减 <span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.couponAmount} placeholder="0" disabled={!promoForm.couponEnabled} onChange={(event) => updatePromoText('couponAmount', event.target.value)} /><small>元</small></span></span>
                            <span className="jdc-cfield">承担方 <select value={promoForm.couponBearer} disabled={!promoForm.couponEnabled} onChange={(event) => updatePromoBearer('couponBearer', event.target.value as JdPromotionBearer)}>
                              <option value="merchant">商家承担</option>
                              <option value="platform">平台承担</option>
                            </select></span>
                          </div>
                        </div>
                        {chainDelta('coupon', promoForm.couponEnabled, layerByName.get('店铺优惠券'), '未达门槛')}
                      </div>

                      <div className={promotionRowClass('cross', promoForm.crossEnabled, layerByName.get('跨店满减券'))} style={promotionRowStyle('cross')} data-promotion-key="cross">
                        <div className="jdc-chain-rail"><span className="jdc-chain-dot" /></div>
                        <div className="jdc-chain-main">
                          <div className="jdc-chain-title-row">
                            {promotionDragHandle('cross')}
                            <label className="jdc-tgl"><input type="checkbox" checked={promoForm.crossEnabled} onChange={(event) => updatePromoEnabled('crossEnabled', event.target.checked)} /><i /></label>
                            <span className="jdc-chain-title">跨店满减券</span>
                            {promotionParallelToggle('cross')}
                            <span className="jdc-chain-summary">{promoForm.crossProportional ? '可跨店凑单，按 减额÷门槛 摊到本件' : '达到门槛后只减一次'}</span>
                          </div>
                          <div className="jdc-chain-fields">
                            <span className="jdc-cfield">满 <span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.crossThreshold} placeholder="0" disabled={!promoForm.crossEnabled} onChange={(event) => updatePromoText('crossThreshold', event.target.value)} /><small>元</small></span></span>
                            <span className="jdc-cfield">减 <span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.crossAmount} placeholder="0" disabled={!promoForm.crossEnabled} onChange={(event) => updatePromoText('crossAmount', event.target.value)} /><small>元</small></span></span>
                            <span className="jdc-cfield">方式 <select value={promoForm.crossProportional ? 'proportional' : 'once'} disabled={!promoForm.crossEnabled} onChange={(event) => setPromoForm({ ...promoForm, crossProportional: event.target.value === 'proportional' })}>
                              <option value="once">满减一次</option>
                              <option value="proportional">按每满比例分摊</option>
                            </select></span>
                            <span className="jdc-cfield">承担方 <select value={promoForm.crossBearer} disabled={!promoForm.crossEnabled} onChange={(event) => updatePromoBearer('crossBearer', event.target.value as JdPromotionBearer)}>
                              <option value="platform">平台承担</option>
                              <option value="merchant">商家承担</option>
                            </select></span>
                            <span className="jdc-cfield">承担比例 <span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.crossBearerSharePercent} disabled={!promoForm.crossEnabled} onChange={(event) => updatePromoText('crossBearerSharePercent', event.target.value)} /><small>%</small></span></span>
                          </div>
                        </div>
                        {chainDelta('cross', promoForm.crossEnabled, layerByName.get('跨店满减券'), '不生效')}
                      </div>

                      <div className={promotionRowClass('activity', promoForm.activityEnabled, layerByName.get('折扣活动'))} style={promotionRowStyle('activity')} data-promotion-key="activity">
                        <div className="jdc-chain-rail"><span className="jdc-chain-dot" /></div>
                        <div className="jdc-chain-main">
                          <div className="jdc-chain-title-row">
                            {promotionDragHandle('activity')}
                            <label className="jdc-tgl"><input type="checkbox" checked={promoForm.activityEnabled} onChange={(event) => updatePromoEnabled('activityEnabled', event.target.checked)} /><i /></label>
                            <span className="jdc-chain-title">折扣活动</span>
                            {promotionParallelToggle('activity')}
                            <span className="jdc-chain-summary">如秒杀、大促直降</span>
                          </div>
                          <div className="jdc-chain-fields">
                            <span className="jdc-cfield">折数 <span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.activityZhe} placeholder="如 9.5" disabled={!promoForm.activityEnabled} onChange={(event) => updatePromoText('activityZhe', event.target.value)} /><small>折</small></span></span>
                            <span className="jdc-cfield">承担方 <select value={promoForm.activityBearer} disabled={!promoForm.activityEnabled} onChange={(event) => updatePromoBearer('activityBearer', event.target.value as JdPromotionBearer)}>
                              <option value="platform">平台承担</option>
                              <option value="merchant">商家承担</option>
                            </select></span>
                            <span className="jdc-cfield">承担比例 <span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.activityBearerSharePercent} disabled={!promoForm.activityEnabled} onChange={(event) => updatePromoText('activityBearerSharePercent', event.target.value)} /><small>%</small></span></span>
                          </div>
                        </div>
                        {chainDelta('activity', promoForm.activityEnabled, layerByName.get('折扣活动'), '不生效')}
                      </div>

                      <div className={promotionRowClass('subsidy', promoForm.subsidyEnabled, layerByName.get('政府补贴'))} style={promotionRowStyle('subsidy')} data-promotion-key="subsidy">
                        <div className="jdc-chain-rail"><span className="jdc-chain-dot" /></div>
                        <div className="jdc-chain-main">
                          <div className="jdc-chain-title-row">
                            {promotionDragHandle('subsidy')}
                            <label className="jdc-tgl"><input type="checkbox" checked={promoForm.subsidyEnabled} onChange={(event) => updatePromoEnabled('subsidyEnabled', event.target.checked)} /><i /></label>
                            <span className="jdc-chain-title">政府补贴</span>
                            {promotionParallelToggle('subsidy')}
                            <span className="jdc-chain-summary">一般8.5折</span>
                          </div>
                          <div className="jdc-chain-fields">
                            <span className="jdc-cfield">折数 <span className="jdc-box"><input type="text" inputMode="decimal" value={promoForm.subsidyZhe} placeholder="如 9.5" disabled={!promoForm.subsidyEnabled} onChange={(event) => updatePromoText('subsidyZhe', event.target.value)} /><small>折</small></span></span>
                            <span className="jdc-cfield">承担方 <select value={promoForm.subsidyBearer} disabled={!promoForm.subsidyEnabled} onChange={(event) => updatePromoBearer('subsidyBearer', event.target.value as JdPromotionBearer)}>
                              <option value="platform">平台承担</option>
                              <option value="merchant">商家承担</option>
                            </select></span>
                          </div>
                        </div>
                        {chainDelta('subsidy', promoForm.subsidyEnabled, layerByName.get('政府补贴'), '不生效')}
                      </div>
                    </div>

                    <div className="jdc-outcome">
                      <div className="jdc-po">
                        <span>{promotionCpsOutcome && promotionCpsOutcome.couponDiscount > 0 ? 'CPS用户到手价' : '用户到手价'}</span>
                        <strong>{money(promotionCpsOutcome?.takeHomePrice ?? promotion.takeHomePrice)}</strong>
                        <small>{promotionCpsOutcome && promotionCpsOutcome.couponDiscount > 0
                          ? `普通/直投 ${money(promotion.takeHomePrice)} · 专享券 −${money(promotionCpsOutcome.couponDiscount)}`
                          : promotion.layers.length ? `共让利 ${money(promotion.totalDiscount)}` : '未启用促销'}</small>
                      </div>
                      <div className="jdc-po">
                        <span>商家承担让利</span>
                        <strong>{money(promotionCpsOutcome?.merchantBorneDiscount ?? promotion.merchantBorneDiscount)}</strong>
                        <small>平台承担 {money(promotion.platformBorneDiscount)}</small>
                      </div>
                      <div className="jdc-po is-hero">
                        <span>{promotionCpsOutcome && promotionCpsOutcome.couponDiscount > 0 ? 'CPS商家实收' : '商家实收'}</span>
                        <strong>{money(promotionCpsOutcome?.merchantReceivable ?? promotion.merchantReceivable)}</strong>
                        <small>{promotionCpsOutcome && promotionCpsOutcome.couponDiscount > 0
                          ? `普通/直投 ${money(promotion.merchantReceivable)}`
                          : '到手价＋平台承担让利'}</small>
                      </div>
                      <button type="button" className={`jdc-apply${promotionApplied ? ' is-applied' : ''}`} onClick={applyPromotion}>
                        {promotionApplied
                          ? promotionCpsOutcome && promotionCpsOutcome.couponDiscount > 0 ? '✓ 已应用基础促销价' : '✓ 已应用为售价'
                          : promotionCpsOutcome && promotionCpsOutcome.couponDiscount > 0 ? '应用基础促销价 →' : '应用为售价 →'}
                      </button>
                    </div>
                  </div>
                </section>
              )}

              <section className="jdc-step">
                <div className="jdc-step-head">
                  <span className="jdc-step-no">2</span>
                  <h2>成本参数</h2>
                  <span className="jdc-step-sub">默认取SKU级成本版本与店铺配置，仅本页测算</span>
                  <button type="button" className="jdc-step-action" onClick={() => defaults && setInputs({ ...defaults.inputs })} disabled={!edited}>
                    <RotateCcw size={13} /> 重置为当前配置
                  </button>
                </div>
                <div className="jdc-step-body">
                  <div className="jdc-price-row">
                    <label className={`jdc-f${defaults && inputs.price !== defaults.inputs.price ? ' is-edited' : ''}`}>
                      <span className="jdc-f-label"><i className="jdc-dot" />售价（成交口径）</span>
                      <span className="jdc-box"><input type="number" min={0} step="0.01" value={inputs.price} onChange={(event) => updateInput('price', event.target.value)} /><small>元</small></span>
                    </label>
                    <div>
                      {appliedTakeHome !== null && (
                        <span className="jdc-applied-chip">✓ 已采用基础促销：普通/直投到手 {money(appliedTakeHome)}、实收 {money(inputs.price)}{cpsCouponDiscount > 0 ? `；CPS到手 ${money(cpsCustomerPrice - cpsCouponDiscount)}、实收 ${money(Math.max(inputs.price - cpsCouponDiscount, 0))}` : '，CPS据此计佣'}</span>
                      )}
                      <span className="jdc-note">默认最近30天件单价；可手改，或点上方「应用为售价」采用促销实收</span>
                    </div>
                  </div>
                  <div className="jdc-groups">
                    <div className="jdc-group">
                      <div className="jdc-group-head">
                        <h3>履约成本</h3><span className="jdc-group-sub">每件发货即发生</span>
                        <span className="jdc-group-total">小计 <b>{money(result.fulfillmentCost)}</b></span>
                      </div>
                      <div className="jdc-fields">
                        {fulfillmentFields.map(({ key, label, unit }) => (
                          <label key={key} className={`jdc-f${defaults && inputs[key] !== defaults.inputs[key] ? ' is-edited' : ''}`}>
                            <span className="jdc-f-label"><i className="jdc-dot" />{label}</span>
                            <span className="jdc-box"><input type="number" min={0} step="0.01" value={inputs[key]} onChange={(event) => updateInput(key, event.target.value)} /><small>{unit}</small></span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="jdc-group">
                      <div className="jdc-group-head">
                        <h3>费率</h3><span className="jdc-group-sub">按售价 / 产品成本计</span>
                        <span className="jdc-group-total">扣点＋税点 <b>{money(result.rateFee)}</b> · 返点 <b>＋{money(result.rebateAmount)}</b></span>
                      </div>
                      <div className="jdc-fields">
                        {rateFields.map(({ key, label, unit, hint }) => (
                          <label key={key} className={`jdc-f${defaults && inputs[key] !== defaults.inputs[key] ? ' is-edited' : ''}`}>
                            <span className="jdc-f-label"><i className="jdc-dot" />{label}</span>
                            <span className="jdc-box"><input type="number" min={0} step="0.01" value={inputs[key]} onChange={(event) => updateInput(key, event.target.value)} /><small>{unit}</small></span>
                            {hint && <span className="jdc-note">{hint}</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {afterSalesInputs && afterSalesResult && (
                <section className="jdc-step">
                  <div className="jdc-step-head">
                    <span className="jdc-step-no">3</span>
                    <h2>售后与广告</h2>
                    <span className="jdc-step-sub">折算到每下单1件的期望值，喂给右侧渠道对比</span>
                    <button type="button" className="jdc-step-action" onClick={() => inputs && setAfterSalesInputs(afterSalesDefaultsFor(inputs, 0))}>
                      <RotateCcw size={13} /> 按当前参数重置
                    </button>
                  </div>
                  <div className="jdc-step-body">
                    <div className="jdc-dual">
                      <div className="jdc-group">
                        <div className="jdc-group-head">
                          <h3>退货售后</h3>
                          <span className="jdc-group-total">单件二退损失 <b>{money(afterSalesResult.unitSecondReturnLoss)}</b></span>
                        </div>
                        <div className="jdc-fields">
                          {afterSalesFields.map(({ key, label, unit, hint }) => (
                            <label key={key} className="jdc-f">
                              <span className="jdc-f-label">{label}</span>
                              <span className="jdc-box"><input type="number" min={0} step="0.01" value={afterSalesInputs[key]} onChange={(event) => updateAfterSalesInput(key, event.target.value)} /><small>{unit}</small></span>
                              {hint && <span className="jdc-note">{hint}</span>}
                            </label>
                          ))}
                        </div>
                        <div className="jdc-inline-calc">损失＝往返运费＋包装损耗＋产品成本×货损率＝<b>{money(afterSalesResult.unitSecondReturnLoss)}</b></div>
                      </div>
                      <div className="jdc-group">
                        <div className="jdc-group-head">
                          <h3>广告投放</h3>
                          <span className="jdc-group-sub">到手回款按已填写费用合计，右侧利润仍分渠道测算</span>
                        </div>
                        <div className="jdc-fields">
                          <label className="jdc-f">
                            <span className="jdc-f-label">单件广告费（直投）</span>
                            <span className="jdc-box"><input type="number" min={0} step="0.01" value={adSpendText} placeholder="0.00" onChange={(event) => setAdSpendText(event.target.value)} /><small>元</small></span>
                            <span className="jdc-note">京准通 / 快车，按点击扣费</span>
                          </label>
                          <label className="jdc-f">
                            <span className="jdc-f-label">京挑客CPS佣金率</span>
                            <span className="jdc-box"><input type="number" min={0} step="0.1" value={cpsRateText} placeholder="0" onChange={(event) => setCpsRateText(event.target.value)} /><small>%</small></span>
                            <span className="jdc-note">按渠道券后的成交价计佣，退货退佣</span>
                          </label>
                          <label className="jdc-f">
                            <span className="jdc-f-label">京挑客专享券额</span>
                            <span className="jdc-box"><input aria-label="京挑客专享券额" type="number" min={0} step="0.01" value={cpsCouponAmountText} placeholder="0" onChange={(event) => setCpsCouponAmountText(event.target.value)} /><small>元</small></span>
                            <span className="jdc-note">留空或0表示不设券；填写后每笔CPS成交直接使用，默认商家承担</span>
                          </label>
                        </div>
                        <div className="jdc-inline-calc">直投费、京挑客佣金和专享券均为填写后才进入到手回款；两项广告费都填时合计扣除。右侧<b>渠道对比</b>仍按各自结算方式展示利润。</div>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {cashRecovery && afterSalesInputs && (
                <div className="jdc-recovery-card jdc-recovery-wide">
                  <h3>到手回款估算 <span className="jdc-h-sub">按已填写广告费合计</span></h3>
                  <div className="jdc-recovery-flow">
                    <div className="jdc-recovery-row is-formula">
                      <div className="jdc-recovery-formula-head">
                        <span><i>1</i><b>每清完1只预计变现</b><small>发生二退就继续卖，直到最终成功售出</small></span>
                        {hasCompleteRecoveryCosts && cashRecovery.expectedCashRecoveryPerClearedUnit !== null
                          ? <strong className={cashRecovery.expectedCashRecoveryPerClearedUnit < 0 ? 'is-negative' : ''}>{money(cashRecovery.expectedCashRecoveryPerClearedUnit)}</strong>
                          : <strong>—</strong>}
                      </div>
                      <div className="jdc-recovery-formula">
                        <span className="jdc-formula-term is-income"><b>{money(cashRecovery.clearedUnitBreakdown.price)}</b><small>商家实收<br />{cpsCouponDiscount > 0 ? '已扣京挑客券' : '成交口径'}</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.platformFee)}</b><small>平台扣点<br />{money(cashRecovery.clearedUnitBreakdown.price)}×{inputs.platformFeeRatePercent}%</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.taxFee)}</b><small>税费<br />{money(cashRecovery.clearedUnitBreakdown.price)}×{inputs.taxRatePercent}%</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.packagingCost)}</b><small>包装费</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.giftCost)}</b><small>赠品</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.shippingInsuranceCost)}</b><small>运费险</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.initialShippingCost)}</b><small>首次发货运费</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.expectedAdvertisingCost)}</b><small>清完预计广告费<br />{recoveryAdvertisingFormula || '未填写，按0元'}</small></span>
                        <em>－</em><span className="jdc-formula-term"><b>{money(cashRecovery.clearedUnitBreakdown.expectedRetryCashCost)}</b><small>二退重试支出<br />({money(afterSalesInputs.returnShippingCost)}＋{money(afterSalesInputs.packagingLossCost)})×{afterSalesInputs.secondReturnRatePercent}%÷{100 - afterSalesInputs.secondReturnRatePercent}%</small></span>
                        <em>＝</em><span className="jdc-formula-term is-result"><b>{cashRecovery.expectedCashRecoveryPerClearedUnit === null ? '—' : money(cashRecovery.expectedCashRecoveryPerClearedUnit)}</b><small>每清完1只预计变现</small></span>
                      </div>
                      <p>产品成本 {money(inputs.productCost)} 是已经投入的库存成本，不在本次现金回款中重复扣除。</p>
                    </div>
                    <div className="jdc-recovery-row is-final">
                      <span><i>2</i><b>全部清完预计总变现</b><small>{inventoryQuantity === null ? '缺ERP可用库存' : `ERP可用库存 ${inventoryQuantity.toLocaleString('zh-CN')} 件，退回后继续卖`}</small></span>
                      {hasCompleteRecoveryCosts && cashRecovery.estimatedInventoryCashRecovery !== null
                        ? <strong className={cashRecovery.estimatedInventoryCashRecovery < 0 ? 'is-negative' : ''}>{money(cashRecovery.estimatedInventoryCashRecovery)}</strong>
                        : <strong>—</strong>}
                    </div>
                  </div>
                  <p className="jdc-footnote">
                    净变现表示这批库存还能换回多少现金，不扣已经投入的产品成本 {money(inputs.productCost)}；利润或亏损仍看右侧“单件测算”。售后期望按二退率 {afterSalesInputs.secondReturnRatePercent}% 计算；总变现假设退回库存继续销售，直到全部成功卖出。
                    {hasRecoveryAdvertisingInput
                      ? ' 直投费与京挑客佣金按本页已填写值合计扣除；京挑客专享券同时进入券后实收和佣金基数。'
                      : ' 当前未填写直投费、京挑客佣金或专享券，广告成本按0元计算。'}
                    {!hasCompleteRecoveryCosts ? ' 当前店铺成本不完整，变现金额暂不展示。' : ''}
                  </p>
                </div>
              )}
            </div>

            <aside className="jdc-rail">
              <div className="jdc-card">
                <h3>利润解剖 <span className="jdc-h-sub">售价 {money(inputs.price)} 拆解</span></h3>
                {inputs.price > 0 ? (
                  <>
                    <div className="jdc-anatomy-bar">
                      <div className="seg jdc-seg-cost" style={{ width: `${Math.max(0, Math.min(100, inputs.productCost / inputs.price * 100)).toFixed(1)}%` }} />
                      <div className="seg jdc-seg-fulfill" style={{ width: `${Math.max(0, Math.min(100, (result.fulfillmentCost - inputs.productCost) / inputs.price * 100)).toFixed(1)}%` }} />
                      <div className="seg jdc-seg-fee" style={{ width: `${Math.max(0, Math.min(100, result.rateFee / inputs.price * 100)).toFixed(1)}%` }} />
                      <div className={`seg ${result.profit >= 0 ? 'jdc-seg-profit' : 'jdc-seg-loss'}`} style={{ width: `${Math.max(0, Math.min(100, Math.abs(result.profit) / inputs.price * 100)).toFixed(1)}%` }} />
                    </div>
                    <div className="jdc-legend">
                      <div className="lg"><i className="jdc-seg-cost" />产品成本<b>{money(inputs.productCost)}</b></div>
                      <div className="lg"><i className="jdc-seg-fulfill" />包装·赠品·运费·运费险<b>{money(result.fulfillmentCost - inputs.productCost)}</b></div>
                      <div className="lg"><i className="jdc-seg-fee" />平台扣点＋税点<b>{money(result.rateFee)}</b></div>
                      <div className="lg"><i className={result.profit >= 0 ? 'jdc-seg-profit' : 'jdc-seg-loss'} />{result.profit >= 0 ? '利润（不含返点）' : '亏损（不含返点）'}<b className={result.profit < 0 ? 'is-negative' : ''}>{money(result.profit)}</b></div>
                      <div className="lg"><small>返点另计：产品成本×{inputs.rebateRatePercent}%＝</small><b style={{ color: 'var(--positive)' }}>＋{money(result.rebateAmount)}</b></div>
                    </div>
                  </>
                ) : (
                  <div className="jdc-empty">售价为0，先在上方填写售价或应用促销实收</div>
                )}
              </div>

              <div className="jdc-card">
                <h3>单件测算 <span className="jdc-h-sub">口径与京东利润中心一致</span></h3>
                <div className="jdc-hero">
                  <div className="jdc-hm"><span>含返点利润</span><strong className={result.profitWithRebate < 0 ? 'is-negative' : ''}>{money(result.profitWithRebate)}</strong></div>
                  <div className="jdc-hm hm-sub"><span>利润率</span><b>{percent(result.profitWithRebateRate)}</b></div>
                  <div className="jdc-hm hm-sub"><span>保本ROI</span><b className="jdc-be">{roi(result.breakEvenRoi)}</b></div>
                </div>
                <div className="jdc-rows">
                  <div className="rr"><span>履约成本</span><b>{money(result.fulfillmentCost)}</b></div>
                  <div className="rr"><span>扣点与税点</span><b>{money(result.rateFee)}</b></div>
                  <div className="rr"><span>预估利润（不含返点）</span><b className={result.profit < 0 ? 'is-negative' : ''}>{money(result.profit)}</b></div>
                  <div className="rr"><span>返点（成本×{inputs.rebateRatePercent}%）</span><b>＋{money(result.rebateAmount)}</b></div>
                </div>
              </div>

              {afterSalesInputs && afterSalesResult && (
                <div className="jdc-card">
                  <h3>最后扣除：退货售后 <span className="jdc-h-sub">二退率 {afterSalesInputs.secondReturnRatePercent}%</span></h3>
                  <div className="jdc-hero">
                    <div className="jdc-hm"><span>自然/直投售后影响 / 每下单件</span><strong style={{ fontSize: 23 }} className={afterSalesResult.afterSalesProfitAdjustmentPerOrdered < 0 ? 'is-negative' : ''}>{signedMoney(afterSalesResult.afterSalesProfitAdjustmentPerOrdered)}</strong></div>
                    <div className="jdc-hm hm-sub"><span>考虑售后利润</span><b className={afterSalesResult.expectedProfitPerOrdered < 0 ? 'is-negative' : ''}>{money(afterSalesResult.expectedProfitPerOrdered)}</b></div>
                    <div className="jdc-hm hm-sub"><span>售后保本ROI</span><b className="jdc-be">{roi(afterSalesResult.afterSalesBreakEvenRoi)}</b></div>
                  </div>
                  <div className="jdc-rows">
                    <div className="rr"><span>单件二退损失</span><b>{money(afterSalesResult.unitSecondReturnLoss)}</b></div>
                    <div className="rr"><span>自然/直投成交利润折损（{money(result.profitWithRebate)}×{afterSalesInputs.secondReturnRatePercent}%）</span><b>−{money(afterSalesResult.expectedProfitForegonePerOrdered)}</b></div>
                    <div className="rr"><span>二退额外期望成本</span><b>−{money(afterSalesResult.expectedLossPerOrdered)}</b></div>
                    {adSimulation && cpsExpectedProfitForegonePerOrdered !== null && (
                      <>
                        <div className="rr"><span>京挑客成交利润折损（{money(adSimulation.cpsProfitPerCompleted)}×{afterSalesInputs.secondReturnRatePercent}%）</span><b>−{money(cpsExpectedProfitForegonePerOrdered)}</b></div>
                        <div className="rr is-channel-total"><span>京挑客售后期望影响（（{money(adSimulation.cpsProfitPerCompleted)}＋{money(afterSalesResult.unitSecondReturnLoss)}）×{afterSalesInputs.secondReturnRatePercent}%）</span><b className={adSimulation.cpsAfterSalesAdjustmentPerOrdered < 0 ? 'is-negative' : ''}>{signedMoney(adSimulation.cpsAfterSalesAdjustmentPerOrdered)}</b></div>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="jdc-card">
                <h3>广告渠道对比 <span className="jdc-h-sub">结算互斥，分渠道单件利润</span></h3>
                <div className="jdc-channels">
                  <div className="jdc-channel">
                    <span className="ch-name">自然流量单</span>
                    <span className="ch-note">不付任何广告费</span>
                    <div className="ch-profit"><b className={adSimulation && adSimulation.organicProfitPerOrdered < 0 ? 'is-negative' : ''}>{adSimulation ? money(adSimulation.organicProfitPerOrdered) : '—'}</b><span>考虑售后利润</span></div>
                    {adSimulation && <div className="ch-after-sales"><span>成功成交利润</span><b className={adSimulation.organicProfitPerCompleted < 0 ? 'is-negative' : ''}>{money(adSimulation.organicProfitPerCompleted)}</b><span>售后期望影响（二退{afterSalesInputs?.secondReturnRatePercent ?? 0}%）</span><b className={adSimulation.organicAfterSalesAdjustmentPerOrdered < 0 ? 'is-negative' : ''}>{signedMoney(adSimulation.organicAfterSalesAdjustmentPerOrdered)}</b></div>}
                  </div>
                  <div className="jdc-channel">
                    <span className="ch-name">直投单 · 京准通/快车</span>
                    <span className="ch-note">已扣单件直投费 {money(adSpend)}</span>
                    <div className="ch-profit"><b className={adSimulation && adSimulation.directAdProfitPerOrdered < 0 ? 'is-negative' : ''}>{adSimulation ? money(adSimulation.directAdProfitPerOrdered) : '—'}</b><span>考虑售后利润</span></div>
                    {adSimulation && <div className="ch-after-sales"><span>成功成交利润</span><b className={adSimulation.directAdProfitPerCompleted < 0 ? 'is-negative' : ''}>{money(adSimulation.directAdProfitPerCompleted)}</b><span>售后期望影响（二退{afterSalesInputs?.secondReturnRatePercent ?? 0}%）</span><b className={adSimulation.directAdAfterSalesAdjustmentPerOrdered < 0 ? 'is-negative' : ''}>{signedMoney(adSimulation.directAdAfterSalesAdjustmentPerOrdered)}</b></div>}
                    <div className="ch-threshold"><span>考虑售后保本投产比（低于即亏）</span><b className="jdc-be">{adSimulation ? roi(adSimulation.breakEvenDirectRoi) : '—'}</b></div>
                  </div>
                  <div className="jdc-channel">
                    <span className="ch-name">CPS单 · 京挑客</span>
                    <span className="ch-note">
                      {cpsCouponDiscount > 0
                        ? `专享券每成交件 −${money(cpsCouponDiscount)}，券后到手 ${money(cpsCustomerPrice - cpsCouponDiscount)}；`
                        : ''}
                      已扣成交佣金 {adSimulation ? money(adSimulation.cpsCommissionPerCompleted) : '—'}（{cpsCouponDiscount > 0 || appliedTakeHome !== null ? '券后到手价' : '成交价'}×{cpsRateText || 0}%）
                    </span>
                    <div className="ch-profit"><b className={adSimulation && adSimulation.cpsProfitPerOrdered < 0 ? 'is-negative' : ''}>{adSimulation ? money(adSimulation.cpsProfitPerOrdered) : '—'}</b><span>考虑售后利润</span></div>
                    {adSimulation && <div className="ch-after-sales"><span>成功成交利润</span><b className={adSimulation.cpsProfitPerCompleted < 0 ? 'is-negative' : ''}>{money(adSimulation.cpsProfitPerCompleted)}</b><span>售后期望影响（二退{afterSalesInputs?.secondReturnRatePercent ?? 0}%）</span><b className={adSimulation.cpsAfterSalesAdjustmentPerOrdered < 0 ? 'is-negative' : ''}>{signedMoney(adSimulation.cpsAfterSalesAdjustmentPerOrdered)}</b></div>}
                    <div className="ch-threshold"><span>考虑售后保本佣金率（高于即亏）</span><b className="jdc-be">{adSimulation && adSimulation.breakEvenCpsRatePercent !== null ? `${adSimulation.breakEvenCpsRatePercent.toFixed(1)}%` : '—'}</b></div>
                  </div>
                </div>
              </div>

              <div className="jdc-card jdc-save">
                <label className="jdc-record-note-field">
                  <span>备注名称 <small>可选，用于区分不同测算方案</small></span>
                  <input
                    type="text"
                    value={recordNoteText}
                    maxLength={80}
                    placeholder="例如：8月清仓价 / 京挑客专享券方案"
                    onChange={(event) => {
                      setRecordNoteText(event.target.value)
                      setSaveMessage('')
                    }}
                  />
                </label>
                {hasLoadedEditableRecord && (
                  <button type="button" className="primary-button" onClick={() => void saveCurrent('overwrite')} disabled={recordsBusy}>
                    <Save size={14} /> {recordsBusy ? '保存中…' : hasLoadedOwnRecord ? '保存（覆盖原记录）' : '保存共享记录'}
                  </button>
                )}
                <button
                  type="button"
                  className={hasLoadedRecord ? 'jdc-btn-ghost' : 'primary-button'}
                  onClick={() => void saveCurrent('new')}
                  disabled={recordsBusy || Boolean(hasLoadedRecord && !loadedRecord?.canCopy)}
                >
                  {hasLoadedOwnRecord
                    ? <><SaveAll size={14} /> 另存为新记录</>
                    : hasLoadedRecord
                      ? <><SaveAll size={14} /> 另存为我的记录</>
                    : <><Save size={14} /> {recordsBusy ? '保存中…' : '保存本次测算'}</>}
                </button>
                {hasLoadedOwnRecord && loadedRecord && (
                  <button type="button" className="jdc-btn-ghost jdc-share-button" onClick={() => setShareRecord(loadedRecord)}><Share2 size={14} />分享{loadedRecord.shareCount ? `（${loadedRecord.shareCount}人）` : ''}</button>
                )}
                {conflictRevision !== null && <div className="jdc-conflict">这条测算已被别人更新到版本 {conflictRevision}，本次没有覆盖。<button type="button" onClick={() => { void refreshRecords().then((next) => { const latest = next.find((item) => item.id === loadedRecordId); if (latest) loadRecord(latest) }) }}>载入最新版本</button></div>}
                {saveMessage && <div className="form-success">{saveMessage}</div>}
                <p className="jdc-footnote">
                  测算为单件静态估算，促销与京挑客渠道券以本页设置为准，未分摊品牌合约；CPS佣金实际以京东联盟结算为准。真实利润以利润中心与SKU详情为准。
                </p>
              </div>
            </aside>
          </div>
        </>
      )}

      <section className="overview-section jdc-history">
        <div className="section-heading-row">
          <div><h2>历史测算记录</h2><p>{recordScope === 'mine' ? '默认只显示当前账号保存的测算；保存后可定向分享。' : '这里只显示别人明确分享给你的测算。'}</p></div>
          <History size={18} aria-hidden />
        </div>
        <div className="jdc-history-tabs" role="tablist" aria-label="历史测算记录范围">
          <button type="button" role="tab" aria-selected={recordScope === 'mine'} className={recordScope === 'mine' ? 'is-active' : ''} onClick={() => setRecordScope('mine')}>我的测算 <span>{ownRecords.length}</span></button>
          <button type="button" role="tab" aria-selected={recordScope === 'shared'} className={recordScope === 'shared' ? 'is-active' : ''} onClick={() => setRecordScope('shared')}>分享给我的 <span>{sharedRecords.length}</span></button>
        </div>
        {recordsError && <div className="form-error">{recordsError}</div>}
        {visibleRecords.length === 0 && !recordsError && <div className="jdc-empty">{recordScope === 'mine' ? '当前账号还没有保存过测算记录；测算后点击「保存本次测算」。' : '还没有人向你分享测算记录。'}</div>}
        {visibleRecords.length > 0 && (
          <div className="table-responsive">
            <table>
              <thead><tr><th>计算ID</th><th>备注名称</th><th>更新时间</th><th>所有者</th><th>权限</th><th>最后编辑</th><th>商品</th><th>售价</th><th>含返点利润</th><th style={{ textAlign: 'right' }}>操作</th></tr></thead>
              <tbody>
                {visibleRecords.map((record) => {
                  const recordResult = computePinduoduoUnitProfit(record.inputs)
                  return (
                    <tr key={record.id}>
                      <td>
                        {record.code
                          ? <span className="jdc-code" role="button" title="点击复制计算ID" onClick={() => copyRecordCode(record.code!)}>{copiedCode === record.code ? '已复制' : record.code}</span>
                          : '—'}
                      </td>
                      <td className="jdc-h-note" title={record.note || ''}>{record.note || '—'}</td>
                      <td className="jdc-h-time">{record.updatedAt ? new Date(record.updatedAt * 1000).toLocaleString('zh-CN', { hour12: false }).slice(0, 16) : savedAtText(record.savedAt)}</td>
                      <td><span className="jdc-owner-badge">{record.createdByName || '未知账号'}</span></td>
                      <td><span className={`calculator-access-badge is-${record.accessLevel || (record.isMine ? 'owner' : 'read')}`}>{record.isMine ? '所有者' : record.accessLevel === 'edit' ? '可编辑' : '只读'}</span></td>
                      <td><span className="jdc-owner-badge">{record.lastEditedByName || record.createdByName || '未知账号'}</span></td>
                      <td>
                        <div className="jdc-h-product">
                          {record.previewImage ? <img src={record.previewImage} alt="" loading="lazy" /> : <span className="jdc-bar-noimg">无图</span>}
                          <div className="jdc-h-name">
                            <strong title={record.productName}>{record.productName}</strong>
                            <small>{record.variantAttributes || '默认规格'} · SKU {record.variantId}{record.merchantVariantCode ? ` · 商家SKU ${record.merchantVariantCode}` : ''}</small>
                          </div>
                        </div>
                      </td>
                      <td>{money(record.inputs.price)}</td>
                      <td style={recordResult.profitWithRebate < 0 ? { color: 'var(--negative)', fontWeight: 650 } : { color: 'var(--positive)', fontWeight: 650 }}>{money(recordResult.profitWithRebate)}</td>
                      <td className="jdc-h-actions">
                        <button type="button" onClick={() => loadRecord(record)}>{record.id === loadedRecordId ? '已载入' : record.canEdit ? '载入编辑' : '载入查看'}</button>
                        {!record.isMine && <Link to={`/shared-calculations/jd/${encodeURIComponent(record.id)}`}>查看分享页</Link>}
                        {record.canShare && <button type="button" className="jdc-share-button" onClick={() => setShareRecord(record)}><Share2 size={12} /> 分享</button>}
                        {record.canDelete && <button type="button" className="is-danger" onClick={() => deleteRecord(record.id)} disabled={recordsBusy}><Trash2 size={12} /> 删除</button>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {shareRecord && <CalculatorShareDialog platform="jd" record={shareRecord} onClose={() => setShareRecord(null)} onSaved={() => { void refreshRecords() }} />}
    </div>
  )
}
